import { FC, useState } from 'react';
import { Accordion, Button } from '@trussworks/react-uswds';

export interface ValidationError {
  error: string;
  msg: string;
  fileName?: string;
  invalidLayerRows?: Partial<Record<string, number[]>> | null;
  validValues?: string[] | null;
}

interface ValidationErrorContentProps {
  message: string;
  details?: ValidationError[];
}

export const InvalidLayerRows: FC<{ invalidRows: Partial<Record<string, number[]>> }> = ({ invalidRows }) => {
  const [showAll, setShowAll] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const layerEntries = Object.entries(invalidRows).filter(([_, rows]) => (rows?.length ?? 0) > 0);
  const allLayersCount = layerEntries.length;
  const visibleLayers = showAll || allLayersCount <= 5 ? layerEntries : layerEntries.slice(0, 5);
  return (
    <div>
      <div className='text-bold margin-top-1'>Rows with errors:</div>
      <ul>
        {visibleLayers.map(([layer, rows]) => (
          <li key={layer}>
            <strong>Layer {layer}:</strong> {rows?.join(', ')}
          </li>
        ))}
      </ul>
      {allLayersCount > 5 && (
        <Button
          type='button'
          title={showAll ? 'Show less' : `View ${allLayersCount - 5} more`}
          className='usa-button usa-button--unstyled margin-left-1 text-no-underline text-primary'
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? 'Show less' : `View ${allLayersCount - 5} more`}
        </Button>
      )}
    </div>
  );
};

export const ValidationErrorModal: FC<ValidationErrorContentProps> = ({ message, details }) => {
  const accordionItems =
    details?.map((err, i) => {
      const hasRows =
        err.invalidLayerRows && Object.values(err.invalidLayerRows).some((rows) => (rows?.length ?? 0) > 0);
      const hasValidValues = (err.validValues?.length ?? 0) > 0;
      return {
        id: `validation-error-${i}`,
        headingLevel: 'h6' as const,
        title: err.error,
        content: (
          <div>
            <div className='margin-bottom-1'>
              <span className='text-bold'>GDB file:</span> <span>{err.fileName ?? 'Unknown file'}</span>
            </div>
            <div>{err.msg}</div>
            {hasRows && <InvalidLayerRows invalidRows={err.invalidLayerRows!} />}
            {hasValidValues && (
              <>
                <div className='text-bold margin-top-1'>Valid values:</div>
                <ul>
                  {err.validValues!.map((val) => (
                    <li key={val}>{val}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        ),
        expanded: true,
      };
    }) || [];
  return (
    <div className='usa-prose'>
      <p className='margin-bottom-1'>{message}</p>
      <Accordion bordered multiselectable items={accordionItems} />
    </div>
  );
};
